import { createFileRoute } from '@tanstack/react-router'
import { ClockAlertIcon, SearchIcon, TimerIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFinalCalculations, useMidWorkDayCutoff } from '@/api/hooks/useFinalCalculations'
import PageLayout from '@/components/layout/page-layout'
import { ResponsiveTabs } from '@/components/molecules/responsive-tabs'
import { StatCard } from '@/components/molecules/stat-card'
import SlutkalkylTable from '@/components/organisms/slutkalkyl-table'
import {
  isOverKeyToKeyGoal,
  keyToKeyStats,
  matchesRegNr,
  needsUpdate,
} from '@/components/organisms/slutkalkyl-table/slutkalkyl.utils'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { appConfig } from '@/config/app.config'
import { useAppSelector } from '@/redux/hooks'

export const Route = createFileRoute('/_authenticated/slutkalkyler/')({
  component: SlutkalkylerPage,
})

type Tab = 'needsUpdate' | 'updated' | 'all'

function SlutkalkylerPage() {
  const { t, i18n } = useTranslation()
  const activeWorkshopId = useAppSelector(state => state.settings.activeWorkshopId)
  const { data: workOrders, isLoading } = useFinalCalculations(activeWorkshopId ?? '')
  const cutoff = useMidWorkDayCutoff()
  const [tab, setTab] = useState<Tab>('needsUpdate')
  const [search, setSearch] = useState('')

  // Stats cover the whole page, regardless of tab and search.
  const stats = keyToKeyStats(workOrders, cutoff)
  const averageFormatter = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 })

  // Counts follow the search, so each tab tells you what you'd see when switching to it.
  const searched = workOrders.filter(workOrder => matchesRegNr(workOrder, search))
  const byTab: Record<Tab, typeof workOrders> = {
    needsUpdate: searched.filter(workOrder => needsUpdate(workOrder, cutoff)),
    updated: searched.filter(workOrder => !needsUpdate(workOrder, cutoff)),
    all: searched,
  }
  const tabs = (['needsUpdate', 'updated', 'all'] as const).filter(value => byTab[value].length > 0)
  // An empty tab is disabled, so never leave it selected, e.g. once the last row is updated.
  const activeTab = byTab[tab].length > 0 ? tab : (tabs[0] ?? tab)

  return (
    <PageLayout title={t('slutkalkylerPage.title')}>
      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <StatCard
          tone={stats.average !== undefined && isOverKeyToKeyGoal(stats.average) ? 'error' : 'info'}
          icon={<TimerIcon />}
          label={t('slutkalkylerPage.keyToKeyAverage')}
          value={
            stats.average === undefined
              ? '–'
              : t('slutkalkylerPage.averageDays', {
                  count: stats.average,
                  value: averageFormatter.format(stats.average),
                })
          }
        />
        <StatCard
          tone={stats.overGoal > 0 ? 'error' : 'info'}
          icon={<ClockAlertIcon />}
          label={t('slutkalkylerPage.overKeyToKeyGoal', { count: appConfig.keyToKeyGoalDays })}
          value={t('slutkalkylerPage.cars', { count: stats.overGoal })}
        />
      </div>
      <InputGroup className="w-full mb-3 hidden max-md:flex ">
        <InputGroupAddon>
          <SearchIcon />
        </InputGroupAddon>
        <InputGroupInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('slutkalkylerPage.searchPlaceholder')}
        />
      </InputGroup>
      <ResponsiveTabs
        variant="line"
        controlsClassName="mb-4"
        value={activeTab}
        onValueChange={value => setTab(value as Tab)}
        items={(['needsUpdate', 'updated', 'all'] as const).map(value => ({
          value,
          label: t(`slutkalkylerPage.${value}Tab`),
          count: byTab[value].length,
          disabled: byTab[value].length === 0,
        }))}
        right={
          <InputGroup className="w-48 max-md:hidden">
            <InputGroupAddon>
              <SearchIcon />
            </InputGroupAddon>
            <InputGroupInput
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('slutkalkylerPage.searchPlaceholder')}
            />
          </InputGroup>
        }
      >
        <SlutkalkylTable workOrders={byTab[activeTab]} isLoading={isLoading} />
      </ResponsiveTabs>
    </PageLayout>
  )
}
